import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Table } from 'primeng/table';
import { SmartintegService } from '../../../../services/smartinteg.service';
import { ToastService } from '../../../../services/toast.service';
import { mensagemErroApi } from '../../../../utils/mensagem-erro-api';
import { Provedor, TipoProvedor } from '../../../../types/smartinteg';

@Component({
  selector: 'app-provedor-list',
  templateUrl: './provedor-list.component.html',
  styleUrl: './provedor-list.component.css',
  providers: [ConfirmationService]
})
export class ProvedorListComponent implements OnInit {

  @ViewChild('dt') table!: Table;

  provedores: Provedor[] = [];
  loading: boolean = false;
  termoBusca = '';

  // Enum para usar no template
  TipoProvedor = TipoProvedor;

  constructor(
    private smartintegService: SmartintegService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.carregarProvedores();
  }

  /**
   * Carregar lista de provedores
   */
  carregarProvedores(): void {
    this.loading = true;
    this.smartintegService.listarProvedores().subscribe({
      next: (provedores) => {
        this.provedores = provedores;
        this.loading = false;
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao carregar provedores: ' + mensagemErroApi(error));
        this.loading = false;
      }
    });
  }

  /**
   * Navegar para criar novo provedor
   */
  novoProvedor(): void {
    this.router.navigate(['/smartinteg/provedores/novo']);
  }

  /**
   * Navegar para editar provedor
   */
  editarProvedor(provedor: Provedor): void {
    this.router.navigate(['/smartinteg/provedores/editar', provedor.id]);
  }

  /**
   * Navegar para gerenciar conexões do provedor
   */
  gerenciarConexoes(provedor: Provedor): void {
    this.router.navigate(['/smartinteg/provedores', provedor.id, 'conexoes']);
  }

  /**
   * Deletar provedor com confirmação
   */
  deletarProvedor(provedor: Provedor): void {
    // O backend faz soft delete sem cascata: conexões e endpoints continuariam listados e executando
    this.smartintegService.listarConexoesPorProvedor(provedor.id).subscribe({
      next: (conexoes) => {
        if (conexoes.length > 0) {
          this.toastService.alerta(
            'Atenção',
            `O provedor "${provedor.nome}" possui ${conexoes.length} conexão(ões). Exclua as conexões antes de excluir o provedor.`
          );
          return;
        }
        this.confirmarExclusao(provedor);
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao verificar conexões do provedor: ' + mensagemErroApi(error));
      }
    });
  }

  private confirmarExclusao(provedor: Provedor): void {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja deletar o provedor <strong>${provedor.nome}</strong>?<br><br>Esta ação é irreversível e pode afetar processos que utilizam este provedor.`,
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      acceptLabel: 'Sim, Deletar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.loading = true;
        this.smartintegService.deletarProvedor(provedor.id).subscribe({
          next: () => {
            this.toastService.sucesso('Sucesso', 'Provedor deletado com sucesso');
            this.carregarProvedores();
          },
          error: (error) => {
            this.toastService.erro('Erro', 'Erro ao deletar provedor: ' + mensagemErroApi(error));
            this.loading = false;
          }
        });
      }
    });
  }

  /**
   * Obter severity para tag do tipo de provedor
   */
  getTipoSeverity(tipo: TipoProvedor): string {
    switch (tipo) {
      case TipoProvedor.BASEDADOS:
        return 'info';
      case TipoProvedor.APIREST:
        return 'success';
      default:
        return 'secondary';
    }
  }

  /**
   * Obter label amigável para tipo de provedor
   */
  getTipoLabel(tipo: TipoProvedor): string {
    switch (tipo) {
      case TipoProvedor.BASEDADOS:
        return 'Banco de Dados';
      case TipoProvedor.APIREST:
        return 'API REST';
      default:
        return tipo;
    }
  }

  /**
   * Limpar filtros da tabela
   */
  limparFiltros(): void {
    this.termoBusca = '';
    this.table.clear();
  }
}
